import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type { CoachingPartnershipStatus } from '@efdigital/ll-addon-data';
import type { CoacheeEngagement } from '@efdigital/ll-xceed-v2/routes/coaching/coach/coachees/coachee';
import type {
  CoacheeListEntry,
  CoacheePartnershipFields,
  CoacheeSessionFields,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-coachees';
import type { TOC } from '@ember/component/template-only';

interface CoacheeNameCardSignature {
  Args: {
    coachee: CoacheeListEntry<CoacheePartnershipFields, CoacheeSessionFields>;
    engagements: CoacheeEngagement[];
  };
  Blocks: {
    /** Actions for this coachee, shown at the end of the identity row. */
    actions?: [];
  };
  Element: HTMLDivElement;
}

/**
 * "Chosen" is a settled state, so it reads as a solid fill; every other
 * status is still in progress and gets an outline instead. The fill uses
 * `text-bg-primary` so it follows the theme's accent in both colour modes
 * rather than pinning a colour here.
 */
function badgeClass(partnershipStatus: CoachingPartnershipStatus): string {
  return partnershipStatus === 'chosen'
    ? 'text-bg-primary'
    : 'border text-body';
}

function programNames(engagements: CoacheeEngagement[]): string {
  return engagements
    .map((engagement) => engagement.coachingConfig.name.trim())
    .filter((name) => name.length > 0)
    .join(', ');
}

/**
 * Identity header for a coachee's page. Layout matches the shared coach
 * profile name card (`ll-avatar-card`): large avatar, bold name, teal
 * subtitle, and a meta row.
 */
const CoacheeNameCard: TOC<CoacheeNameCardSignature> = <template>
  <div
    class="card border shadow-sm ll-avatar-card"
    data-test-coachee-name-card
    ...attributes
  >
    <div class="card-body p-3 pe-4 pe-lg-5">
      <div
        class="d-flex flex-column flex-md-row gap-3 gap-xxl-5 align-items-center"
      >
        <span
          class="ll-avatar-xl rounded-circle text-bg-primary d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold fs-2"
          aria-hidden="true"
        >{{@coachee.initials}}</span>

        {{! Plain heading rather than `Heading`: it title-cases its text,
            which is not ours to do to someone's name. }}
        <div class="flex-grow-1 min-w-0">
          <h1 class="h3 fw-bold mb-1 text-break">{{@coachee.displayName}}</h1>

          {{#let (programNames @engagements) as |programs|}}
            {{#if programs}}
              <div
                class="d-flex flex-wrap align-items-center column-gap-2 row-gap-0 mb-2"
              >
                <p class="text-primary fw-bold mb-0">{{programs}}</p>
              </div>
            {{/if}}
          {{/let}}

          {{#let
            (getStatusIcon @coachee.status @coachee.hasChemistrySession)
            as |statusDisplay|
          }}
            {{#if statusDisplay}}
              <ul
                class="list-unstyled d-flex flex-wrap align-items-center column-gap-3 row-gap-0 mb-0 small text-body-secondary fw-semibold"
              >
                <li class="d-inline-flex align-items-center gap-1">
                  <span
                    class="badge rounded-pill fw-semibold px-2 py-1 d-inline-flex align-items-center gap-1
                      {{badgeClass @coachee.status}}"
                  >
                    <iconify-icon
                      inline
                      icon={{statusDisplay.icon}}
                      aria-hidden="true"
                    />
                    {{statusDisplay.label}}
                  </span>
                </li>
              </ul>
            {{/if}}
          {{/let}}
        </div>

        {{! Yielded bare so an empty block adds no flex item, and no gap. }}
        {{yield to="actions"}}
      </div>
    </div>
  </div>
</template>;

export default CoacheeNameCard;
